import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Search, CheckCircle2, Circle, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { publicApi } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ReviewForm from "@/components/ReviewForm";
import { BRAND } from "@/lib/brand";

const STATUS_STEPS = [
  { key: "deposit_paid", label: "Deposit received" },
  { key: "awaiting_allocation", label: "Awaiting driver allocation" },
  { key: "confirmed", label: "Confirmed" },
  { key: "collected", label: "Collected" },
  { key: "delivered", label: "Delivered" },
];

const REVIEWABLE_STATUSES = new Set(["collected", "delivered"]);

const money = (n) => (n == null ? "—" : `£${Number(n).toLocaleString("en-GB")}`);

export default function Track() {
  const [ref, setRef] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [existingReview, setExistingReview] = useState(null);

  async function lookup(e) {
    e?.preventDefault();
    if (!ref.trim() || !email.trim()) {
      toast.error("Enter both booking reference and email.");
      return;
    }
    setLoading(true);
    try {
      const cleanRef = ref.trim().toUpperCase();
      const cleanEmail = email.trim();
      const { data } = await publicApi.get(
        `/bookings/${encodeURIComponent(cleanRef)}`,
        { params: { email: cleanEmail } }
      );
      setBooking(data);
      if (REVIEWABLE_STATUSES.has(data.status)) {
        try {
          const { data: rev } = await publicApi.get(
            `/reviews/booking/${encodeURIComponent(cleanRef)}`,
            { params: { email: cleanEmail } }
          );
          setExistingReview(rev?.review || null);
        } catch (_) {
          setExistingReview(null);
        }
      } else {
        setExistingReview(null);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        err?.response?.status === 404
          ? "We couldn't find that booking. Check the reference."
          : err?.response?.status === 403
          ? "The email doesn't match this booking."
          : typeof detail === "string" ? detail : "Lookup failed.";
      toast.error(msg);
      setBooking(null);
      setExistingReview(null);
    } finally {
      setLoading(false);
    }
  }

  const isCancelled = booking && ["cancelled", "refund_required", "refunded"].includes(booking.status);
  const currentIndex = booking ? STATUS_STEPS.findIndex((s) => s.key === booking.status) : -1;

  return (
    <main data-testid="track-page" className="bg-[#050505] text-white min-h-screen">
      <Header />
      <div className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-4">
            Booking Tracker
          </div>
          <h1 className="font-display font-extrabold text-white text-4xl md:text-6xl leading-[1.0] tracking-tight">
            Track your
            <br />
            <span className="text-stroke">transport.</span>
          </h1>
          <p className="text-zinc-400 text-base lg:text-lg mt-4 max-w-xl">
            Enter your booking reference and the email you used to book. We&apos;ll show
            you the latest status.
          </p>

          <form
            onSubmit={lookup}
            className="mt-10 border border-white/10 bg-[#0A0A0A] p-6 md:p-8 space-y-5"
            data-testid="track-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">
                  Booking reference
                </label>
                <input
                  data-testid="track-ref"
                  value={ref}
                  onChange={(e) => setRef(e.target.value)}
                  placeholder="MHJ-10001"
                  className="w-full bg-transparent border border-white/15 text-white px-4 py-3 text-base placeholder:text-zinc-600 focus:outline-none focus:border-[#E60000] transition-colors uppercase tracking-wider"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">
                  Email used to book
                </label>
                <input
                  data-testid="track-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="w-full bg-transparent border border-white/15 text-white px-4 py-3 text-base placeholder:text-zinc-600 focus:outline-none focus:border-[#E60000] transition-colors"
                />
              </div>
            </div>
            <button
              type="submit"
              data-testid="track-submit"
              disabled={loading}
              className="btn-primary disabled:opacity-40"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              Look up my booking
            </button>
          </form>

          {booking && (
            <motion.section
              key={booking.booking_ref}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mt-10 border border-white/10 bg-[#0A0A0A] p-6 md:p-8 space-y-8"
              data-testid="track-result"
            >
              <div className="flex flex-wrap items-end justify-between gap-4 pb-6 border-b border-white/10">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
                    Booking reference
                  </div>
                  <div className="font-display font-extrabold text-3xl md:text-4xl mt-2">
                    {booking.booking_ref}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">Status</div>
                  <div className={`font-display font-bold text-lg md:text-xl mt-2 ${isCancelled ? "text-[#E60000]" : "text-white"}`}>
                    {(booking.status || "").replace(/_/g, " ")}
                  </div>
                </div>
              </div>

              {/* Progress timeline */}
              {!isCancelled && (
                <div className="space-y-4">
                  {STATUS_STEPS.map((s, i) => {
                    const done = currentIndex >= i;
                    const active = currentIndex === i;
                    return (
                      <div key={s.key} className="flex items-start gap-4">
                        {done ? (
                          <CheckCircle2 className="w-5 h-5 text-[#E60000] mt-0.5 shrink-0" />
                        ) : (
                          <Circle className={`w-5 h-5 mt-0.5 shrink-0 ${active ? "text-white" : "text-zinc-700"}`} />
                        )}
                        <div>
                          <div className={`text-sm font-semibold ${done ? "text-white" : "text-zinc-500"}`}>
                            {s.label}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Details */}
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <Cell label="Vehicle">
                  {[booking.vehicle_year, booking.vehicle_make, booking.vehicle_model].filter(Boolean).join(" ") || "—"}
                </Cell>
                <Cell label="Requested collection">
                  {booking.requested_collection_date || "—"} · {booking.requested_collection_time || "—"}
                </Cell>
                <Cell label="Route">{booking.pickup_location} → {booking.dropoff_location}</Cell>
                <Cell label="Transport price">{money(booking.instant_booking_price)}</Cell>
                <Cell label="Deposit paid">{money(booking.deposit_paid || booking.deposit_due)}</Cell>
                <Cell label="Balance">{money(booking.balance_remaining)}</Cell>
              </div>

              <div className="pt-6 border-t border-white/10 flex flex-wrap gap-3">
                <a href={`tel:${BRAND.phoneRaw}`} className="btn-primary" data-testid="track-call">
                  <Phone className="w-4 h-4" /> Call {BRAND.phone}
                </a>
                <a href={`mailto:${BRAND.email}?subject=Booking ${booking.booking_ref}`} className="btn-ghost" data-testid="track-email-us">
                  <Mail className="w-4 h-4" /> Email us
                </a>
              </div>
            </motion.section>
          )}

          {booking && REVIEWABLE_STATUSES.has(booking.status) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-8"
            >
              <ReviewForm
                key={existingReview?.review_id || `${booking.booking_ref}-new`}
                bookingRef={booking.booking_ref}
                email={email.trim()}
                defaultName={booking.customer_name}
                existingReview={existingReview}
                onSubmitted={() => setExistingReview({ ...(existingReview || {}), submitted: true })}
              />
            </motion.div>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
}

function Cell({ label, children }) {
  return (
    <div className="border border-white/10 bg-[#050505] p-4">
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">{label}</div>
      <div className="text-white text-sm mt-1 break-words">{children ?? "—"}</div>
    </div>
  );
}
